import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { BazisMapService } from '@bazis/map/services/map.service';
import { BehaviorSubject, combineLatest, Observable, shareReplay } from 'rxjs';
import {
    LatLng,
    MapDraggedEvent,
    MapLayers,
    MapPopupInfo,
    MapSettings,
} from '@bazis/map/models/map.models';
import * as L from 'leaflet';
import { debounceTime, filter, map, tap } from 'rxjs/operators';
import { EditableMapService } from '@map/services/editable-map.service';

@Component({
    selector: 'app-map-editable',
    templateUrl: 'map-editable.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [BazisMapService],
    standalone: false
})
export class MapEditableComponent implements OnInit {
    @Input()
    tileMode: 'default' | 'grey' = 'default';

    @Input()
    mapSettings$: Observable<MapSettings>;

    @Input()
    mapLayers$: Observable<MapLayers>;

    @Input()
    selectOnMap$: Observable<{ layerId: string; objectId: string; openPopup?: boolean }>;

    @Input()
    popupInfo$: Observable<MapPopupInfo>;

    @Input()
    fitLayersBounds$: Observable<{ layers: string[]; maxZoom?: number }>;

    @Input()
    containerResized$: Observable<boolean>;

    @Input()
    centerMapOnCoordinates$: Observable<{
        lat: number;
        lng: number;
        minZoom?: number;
        animationDuration?: number;
    }>;

    @Input()
    openModalControlEnabled = false;

    @Input()
    tileControlEnabled = true;

    @Input()
    zoomControlEnabled = true;

    @Input()
    myLocationEnabled = true;

    @Input()
    lassoEnabled = true;

    @Input()
    drawPolygonEnabled = true;

    @Input()
    drawLineEnabled = true;

    @Input()
    transformEnabled = true;

    @Input()
    transformSettings = {
        union: true,
    };

    @Output()
    selected = new EventEmitter();

    @Output()
    groupSelected = new EventEmitter();

    @Output()
    popupAction = new EventEmitter();

    @Output()
    dragged = new EventEmitter();

    @Output()
    lassoSelected = new EventEmitter();

    @ViewChild('mapElement', { static: true }) mapElement: ElementRef;

    settings$: Observable<MapSettings>;

    resized$: Observable<boolean>;

    select$: Observable<{ layerId: string; objectId: string }>;

    centerOnCoordinates$: Observable<LatLng>;

    layers$: Observable<MapLayers>;

    map$: Observable<L.Map>;

    selectedTileId = '';

    mapSelected$ = this.mapService.selected.$.pipe(
        tap((options) => {
            console.log('next selected on map', options);
            this.selected.emit(options);
        }),
        shareReplay(),
    );

    // mapGroupSelected$ = this.mapService.groupSelected.$.pipe(
    //     tap((options) => {
    //         console.log('next selectedGroup on map', options);
    //         this.groupSelected.emit(options);
    //     }),
    //     shareReplay(),
    // );

    popupActions$ = this.mapService.popupActions.$.pipe(
        tap((popupAction) => {
            console.log('action from popup', popupAction);
            this.popupAction.emit(popupAction);
        }),
    );

    dragged$ = this.mapService.dragged.$.pipe(
        tap((e: MapDraggedEvent) => {
            this.dragged.emit(e);
        }),
    );

    executeFitLayersBounds$;

    constructor(public mapService: EditableMapService) {}

    ngOnInit() {
        this.settings$ = this.mapSettings$.pipe(
            tap((options) => {
                this.mapService.initMap(this.mapElement.nativeElement, options);
                this.selectedTileId = this.mapService.getInitialTileSettings(options).id;
            }),
            shareReplay(),
        );

        this.layers$ = this.mapLayers$.pipe(
            debounceTime(2),
            tap((layerGroups) => {
                this.mapService.setLayers(layerGroups);
            }),
            shareReplay(),
        );

        this.select$ = this.selectOnMap$?.pipe(
            tap((params) => {
                if (params) {
                    this.mapService.selectById(params.layerId, params.objectId, params.openPopup);
                }
            }),
            shareReplay(),
        );

        this.centerOnCoordinates$ = this.centerMapOnCoordinates$?.pipe(
            filter((v) => !!v),
            tap((params) => {
                if (params.animationDuration === undefined) {
                    params.animationDuration = 0.35;
                }
                this.mapService.centerOnCoordinates(
                    params.lat,
                    params.lng,
                    params.minZoom,
                    params.animationDuration,
                );
            }),
            shareReplay(),
        );

        this.map$ = this.settings$.pipe(map(() => this.mapService.map));

        if (!this.containerResized$) {
            this.containerResized$ = new BehaviorSubject(false);
        }

        this.resized$ = combineLatest([this.containerResized$, this.map$]).pipe(
            debounceTime(0),
            map((v) => {
                if (this.mapService.map) this.mapService.map.invalidateSize();
                return true;
            }),
        );

        if (this.fitLayersBounds$) {
            this.executeFitLayersBounds$ = this.fitLayersBounds$.pipe(
                tap((fitBoundsSettings) => {
                    this.mapService.fitLayersBounds(
                        fitBoundsSettings.layers,
                        fitBoundsSettings.maxZoom || 7,
                    );
                }),
            );
        }
    }

    setTileLayer(tile) {
        this.mapService.setTileLayer(tile);
        this.selectedTileId = tile.id;
    }
}
